import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { CollapseProps } from 'antd';
import { Empty, Card, Typography, Tag, Spin, Alert, Button, Space, Collapse } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import { diffLines, formatLines } from 'unidiff';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
  ListRowProps,
} from 'react-virtualized';
import dayjs from 'dayjs';
import {
  CREATE,
  DELETE,
  CREATE_ACTION_COLOR,
  UPDATE_ACTION_COLOR,
  DELETE_ACTION_COLOR,
} from '../../../constants/ChangeLogConstants';
import { ApplicationState } from '../../../reducers';
import { ChangeLogEntry } from '../../../../types/change_logs';
import { getPageLimit } from '../../../utils/Utils';

const { Text } = Typography;

const defaultDiffViewHeight = 280;
const pageSize = getPageLimit();

type ChangeLogDiffPageProps = {
  hasMoreData: boolean;
  handleFetchNext: (params: { startIndex: number; stopIndex: number }) => Promise<void>;
  handleFilterSearch: () => void;
};

const cache: CellMeasurerCache = new CellMeasurerCache({
  defaultHeight: defaultDiffViewHeight,
  fixedWidth: true,
});

const ChangeLogDiffPage: React.FC<ChangeLogDiffPageProps> = ({
  hasMoreData,
  handleFetchNext,
  handleFilterSearch,
}) => {
  const { t } = useTranslation();

  const { history, fetchingChangeLogs, error } = useSelector(
    (state: ApplicationState) => state.changeLogs
  );

  const listRef = useRef<List | null>(null);
  const loadedRowsRef = useRef<Set<number>>(new Set());
  const previousHistoryLengthRef = useRef<number>(0);

  useEffect(() => {
    if (history.length < previousHistoryLengthRef.current || history.length === 0) {
      cache.clearAll();
      loadedRowsRef.current.clear();
      if (listRef.current) {
        listRef.current.forceUpdateGrid();
      }
    }
    previousHistoryLengthRef.current = history.length;
  }, [history.length]);

  const handleToggle = (index: number) => {
    cache.clear(index, 0);
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.recomputeRowHeights(index);
      }
    }, 200);
  };

  const handleLoadMoreRows = (params: { startIndex: number; stopIndex: number }) => {
    return handleFetchNext(params).then(() => {
      for (let i = params.startIndex; i <= params.stopIndex; i++) {
        loadedRowsRef.current.add(i);
      }

      cache.clearAll();
      if (listRef.current) {
        listRef.current.recomputeRowHeights();
      }
    });
  };

  const getActionColor = (operation: string) => {
    switch (operation) {
      case CREATE:
        return CREATE_ACTION_COLOR;
      case DELETE:
        return DELETE_ACTION_COLOR;
      default:
        return UPDATE_ACTION_COLOR;
    }
  };

  const formatObjectForDiff = (obj: any): string => {
    if (obj === null || obj === undefined) {
      return '';
    }

    let jsonData = JSON.stringify(obj, null, 2);
    if (jsonData === '{}' || jsonData === '[]') {
      jsonData = '';
    }

    return jsonData;
  };

  const renderDiffView = (before: any, after: any): JSX.Element | null => {
    const beforeText = formatObjectForDiff(before);
    const afterText = formatObjectForDiff(after);

    if (beforeText === afterText) {
      return (
        <div className="change-logs-no-diff">
          <Text type="secondary">{t('changeLog:noDiff')}</Text>
        </div>
      );
    }

    const diffText = formatLines(diffLines(beforeText, afterText), { context: 3 });
    const [diff] = parseDiff(diffText, { nearbySequences: 'zip' });

    const hunks = diff ? diff.hunks : [];

    if (hunks.length === 0) {
      return (
        <div className="change-logs-no-diff">
          <Text type="secondary">{t('changeLog:noDiff')}</Text>
        </div>
      );
    }

    return (
      <div className="custom-diff-viewer">
        <div className="custom-diff-viewer__inner">
          <Diff viewType="split" diffType={diff.type || 'modify'} hunks={hunks}>
            {hunks => hunks?.map(hunk => <Hunk key={hunk.content} hunk={hunk} />)}
          </Diff>
        </div>
      </div>
    );
  };

  const renderDiffItemPartNumbers = (diffPartNumbers: string[]) => {
    return (
      diffPartNumbers && (
        <div className="change-logs-part-numbers">
          <Text strong>{t('changeLog:changedPartNumbers')}</Text>
          <div className="change-logs-part-numbers__list">
            {diffPartNumbers.map((partNumber: string, idx: number) => (
              <Tag key={idx} color="geekblue" className="change-logs-part-numbers__tag">
                {partNumber}
              </Tag>
            ))}
          </div>
        </div>
      )
    );
  };

  const renderDiffHeader = (changedData: ChangeLogEntry) => {
    return (
      <div className="change-logs-diff-header">
        <Tag color="blue">{changedData.update_segments}</Tag>
        <Tag color={getActionColor(changedData.operation_type)}>
          {changedData.operation_type?.toUpperCase() || t('changeLog:unknownChangedBy')}
        </Tag>
      </div>
    );
  };

  const getItems: (
    changedData: ChangeLogEntry,
    segmentBeforeData: any[] | undefined,
    segmentAfterData: any[] | undefined
  ) => CollapseProps['items'] = (changedData, segmentBeforeData, segmentAfterData) => [
    {
      key: 'diffs',
      label: renderDiffHeader(changedData),
      children: (
        <div className="change-logs-collapse-content">
          {renderDiffView(segmentBeforeData, segmentAfterData)}
        </div>
      ),
      className: 'change-logs-collapse-item',
    },
  ];

  const rowRenderer = ({ key, index, parent, style }: ListRowProps) => {
    const changedData = history[index];
    const segmentBeforeData = changedData?.data_before?.[changedData.update_segments] || [];
    const segmentAfterData = changedData?.data_after?.[changedData.update_segments] || [];

    if (!changedData) {
      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          <div style={style}>
            <Spin className="spinner-center" />
          </div>
        </CellMeasurer>
      );
    }

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div style={style}>
          <Card
            title={
              <div className="change-logs-row__title">
                <Space>
                  <Tag color="purple">
                    {t('changeLog:changedBy')} {changedData.changed_by}
                  </Tag>
                </Space>
                <span className="change-logs-row__timestamp">
                  <ClockCircleOutlined className="mr-1" />
                  {dayjs(changedData.changed_at).format('YYYY-MM-DD HH:mm:ss')}
                </span>
              </div>
            }
          >
            {renderDiffItemPartNumbers(changedData.item_part_numbers)}

            <Collapse
              defaultActiveKey={[]}
              items={getItems(changedData, segmentBeforeData, segmentAfterData)}
              onChange={() => {
                handleToggle(index);
              }}
            />
          </Card>
        </div>
      </CellMeasurer>
    );
  };

  if (error) {
    return (
      <Alert
        message={t('changeLog:loadingError')}
        description={error}
        type="error"
        showIcon
        className="change-logs-error"
        action={
          <Button size="small" danger onClick={handleFilterSearch}>
            {t('changeLog:tryAgain')}
          </Button>
        }
      />
    );
  }

  const noRowsRenderer = () => {
    return hasMoreData ? (
      <Spin className="spinner-center" />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  };

  return (
    <div className="change-logs-container__content">
      {(() => {
        if (fetchingChangeLogs && (!history || history.length === 0)) {
          return (
            <Card>
              <div className="change-logs-container__loading">
                <Text type="secondary">{t('changeLog:loading')}</Text>
                <Spin className="change-logs-container__loading-spinner" />
              </div>
            </Card>
          );
        }
        if (!history || history.length === 0) {
          return (
            <Card>
              <div className="change-logs-container__no-history">
                <Text type="secondary">{t('changeLog:noHistory')}</Text>
              </div>
            </Card>
          );
        }
        return (
          <AutoSizer>
            {({ width, height }) => {
              const rowCount = hasMoreData ? history.length + pageSize : history.length;
              return (
                <InfiniteLoader
                  isRowLoaded={({ index }) => !!history[index]}
                  loadMoreRows={handleLoadMoreRows}
                  rowCount={rowCount}
                  minimumBatchSize={pageSize}
                  threshold={5}
                >
                  {({ onRowsRendered }) => (
                    <List
                      className="change-logs-history-list"
                      ref={listRef}
                      width={width}
                      height={height}
                      deferredMeasurementCache={cache}
                      rowCount={rowCount}
                      rowHeight={cache.rowHeight}
                      rowRenderer={rowRenderer}
                      noRowsRenderer={noRowsRenderer}
                      onRowsRendered={onRowsRendered}
                    />
                  )}
                </InfiniteLoader>
              );
            }}
          </AutoSizer>
        );
      })()}
    </div>
  );
};

export default ChangeLogDiffPage;
